import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { GiftIcon, StarIcon } from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import Modal from "../../../components/common/Modal";
import { loyaltyService } from "../services/loyaltyService";

const LoyaltyList = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(null); // user pour reset
  const [showReset, setShowReset] = useState(false);
  const [adjustUser, setAdjustUser] = useState(null);
  const [adjustPoints, setAdjustPoints] = useState("");
  const [adjustReason, setAdjustReason] = useState("");

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["loyalty", page, search],
    queryFn: () => loyaltyService.getOverview({ page, search }),
    refetchInterval: 15000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["loyalty"] });

  const resetMutation = useMutation({
    mutationFn: (userId) => loyaltyService.resetPoints(userId),
    onSuccess: () => {
      toast.success("Points réinitialisés");
      setShowReset(false);
      setTarget(null);
      invalidate();
    },
    onError: () => toast.error("Échec de la réinitialisation"),
  });

  const adjustMutation = useMutation({
    mutationFn: ({ userId, points, reason }) =>
      loyaltyService.adjustPoints(userId, points, reason),
    onSuccess: () => {
      toast.success("Points ajustés");
      setAdjustUser(null);
      setAdjustPoints("");
      setAdjustReason("");
      invalidate();
    },
    onError: () => toast.error("Échec de l'ajustement"),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return <div className="text-red-500">Erreur lors du chargement des points</div>;

  const pending = data?.pending_redemptions || [];
  const users = data?.users?.data || [];
  const meta = data?.users?.meta || {};

  const fullName = (u) => `${u?.firstname || ""} ${u?.lastname || ""}`.trim() || "Client";

  const submitAdjust = () => {
    const n = parseInt(adjustPoints, 10);
    if (Number.isNaN(n) || n === 0) {
      toast.error("Entrez un nombre de points (ex: 50 ou -50)");
      return;
    }
    adjustMutation.mutate({ userId: adjustUser.id, points: n, reason: adjustReason });
  };

  return (
    <div className="space-y-6">
      {/* Demandes d'échange en attente */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-4 border-b border-gray-200 flex items-center gap-2">
          <GiftIcon className="h-6 w-6 text-primary" />
          <h2 className="text-base font-medium text-gray-800">
            Demandes d'échange en attente
          </h2>
          <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
            {pending.length}
          </span>
        </div>
        <div className="p-4">
          {pending.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune demande en attente.</p>
          ) : (
            <div className="space-y-3">
              {pending.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between border border-gray-200 rounded-lg p-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {fullName(r.user)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {r.user?.phone_number} · {r.points} pts demandés
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setTarget(r.user);
                      setShowReset(true);
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 cursor-pointer"
                  >
                    Honorer & réinitialiser
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Utilisateurs avec des points */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <h2 className="text-base font-medium text-gray-800">
            Utilisateurs avec des points
          </h2>
          <input
            type="text"
            placeholder="Rechercher (nom, téléphone)"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-64"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Client</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Téléphone</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Points</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {fullName(u)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {u.phone_number}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-800">
                        <StarIcon className="h-4 w-4 text-amber-500" />
                        {u.loyalty_points}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setAdjustUser(u);
                            setAdjustPoints("");
                            setAdjustReason("");
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          Ajuster
                        </button>
                        <button
                          onClick={() => {
                            setTarget(u);
                            setShowReset(true);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 cursor-pointer"
                        >
                          Réinitialiser
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucun utilisateur avec des points
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={page}
          totalItems={meta.total}
          itemsPerPage={meta.per_page}
          onPageChange={setPage}
        />
      </div>

      <DeleteConfirmation
        isOpen={showReset}
        onClose={() => setShowReset(false)}
        onConfirm={() => target && resetMutation.mutate(target.id)}
        title="Réinitialiser les points"
        message={`Confirmer la remise à zéro des points de ${target ? fullName(target) : ""} ? À faire une fois la récompense remise.`}
        confirmButtonText="Réinitialiser"
        confirmButtonColor="danger"
      />

      <Modal isOpen={!!adjustUser} onClose={() => setAdjustUser(null)}>
        <div className="text-left">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            Ajuster les points {adjustUser ? `— ${fullName(adjustUser)}` : ""}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Nombre positif pour ajouter, négatif pour retirer (ex : 50 ou -50).
          </p>
          <input
            type="number"
            value={adjustPoints}
            onChange={(e) => setAdjustPoints(e.target.value)}
            placeholder="Points (+/-)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3"
          />
          <input
            type="text"
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
            placeholder="Motif (optionnel)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setAdjustUser(null)}
              className="px-4 py-2 text-sm text-gray-600 cursor-pointer"
            >
              Annuler
            </button>
            <button
              onClick={submitAdjust}
              disabled={adjustMutation.isPending}
              className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
            >
              Appliquer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoyaltyList;
